(() => {
  if (customElements.get('aa-size-finder')) return;

  const normalizeValue = (value) => String(value ?? '').trim().toUpperCase();
  const LETTER_SIZES = new Set(['XS', 'S', 'M', 'L']);
  const NUMERIC_SIZES = new Set(['6', '8', '10', '12']);

  class AASizeFinder extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.initialized = true;
      this.sectionId = this.dataset.aaSectionId;
      this.modal = document.getElementById(this.dataset.aaModalId);
      this.liveRegion = this.querySelector('[data-aa-size-live]');
      this.config = this.parseConfig();

      if (!this.modal || !this.config) return;

      this.onModalClick = this.handleModalClick.bind(this);
      this.onDocumentClick = this.handleDocumentClick.bind(this);
      this.onDocumentChange = this.handleDocumentChange.bind(this);

      this.modal.addEventListener('click', this.onModalClick);
      document.addEventListener('click', this.onDocumentClick, true);
      document.addEventListener('change', this.onDocumentChange);
      this.refresh();
    }

    disconnectedCallback() {
      this.modal?.removeEventListener('click', this.onModalClick);
      document.removeEventListener('click', this.onDocumentClick, true);
      document.removeEventListener('change', this.onDocumentChange);
      this.modal?.remove();
    }

    parseConfig() {
      try {
        return JSON.parse(this.querySelector('[data-aa-size-config]')?.textContent || '');
      } catch (error) {
        return null;
      }
    }

    get picker() {
      return document.querySelector(`variant-selects[data-section="${CSS.escape(this.sectionId)}"]`);
    }

    get sizeIndex() {
      return Number(this.config.optionPosition) - 1;
    }

    get sizeSystem() {
      const normalizedValues = this.config.sizeValues.map(normalizeValue);
      const hasLetters = normalizedValues.some((value) => LETTER_SIZES.has(value));
      const hasNumbers = normalizedValues.some((value) => NUMERIC_SIZES.has(value));

      if (hasLetters && hasNumbers) return 'mixed';
      return hasNumbers ? 'numeric' : 'letter';
    }

    getCurrentSelections() {
      if (!this.picker) return [];

      return Array.from(this.picker.querySelectorAll(':scope > .product-form__input')).map((group) => {
        const checkedInput = group.querySelector('input[type="radio"]:checked');
        if (checkedInput) return checkedInput.value;

        const select = group.querySelector('select');
        return select?.value || null;
      });
    }

    getProductSizeValue(button) {
      const acceptedValues = [button.dataset.aaLetter, button.dataset.aaNumber].map(normalizeValue);
      return this.config.sizeValues.find((value) => acceptedValues.includes(normalizeValue(value))) || null;
    }

    getCompatibleVariants(productSizeValue, selections) {
      return this.config.variants.filter((variant) => {
        if (normalizeValue(variant.options[this.sizeIndex]) !== normalizeValue(productSizeValue)) return false;

        return variant.options.every((value, index) => {
          if (index === this.sizeIndex || !selections[index]) return true;
          return normalizeValue(value) === normalizeValue(selections[index]);
        });
      });
    }

    getButtonState(button, selections) {
      const productSizeValue = this.getProductSizeValue(button);
      if (!productSizeValue) {
        return { name: 'unavailable-reference', label: 'No disponible en esta referencia', productSizeValue: null };
      }

      const sizeVariants = this.config.variants.filter(
        (variant) => normalizeValue(variant.options[this.sizeIndex]) === normalizeValue(productSizeValue)
      );
      if (!sizeVariants.length) {
        return { name: 'unavailable-reference', label: 'No disponible en esta referencia', productSizeValue };
      }

      const compatibleVariants = this.getCompatibleVariants(productSizeValue, selections);
      if (!compatibleVariants.length) {
        return { name: 'unavailable-combination', label: 'No disponible en esta combinación', productSizeValue };
      }

      if (!compatibleVariants.some((variant) => variant.available)) {
        return { name: 'sold-out', label: 'Agotada en este color/combinación', productSizeValue };
      }

      return { name: 'available', label: 'Disponible', productSizeValue };
    }

    refresh() {
      if (!this.picker || !this.modal) return;

      const selections = this.getCurrentSelections();
      const currentSizeValue = selections[this.sizeIndex];

      this.modal.querySelectorAll('[data-aa-action="size-select"]').forEach((button) => {
        const state = this.getButtonState(button, selections);
        const isSelected =
          state.productSizeValue && normalizeValue(state.productSizeValue) === normalizeValue(currentSizeValue);

        button.dataset.aaState = state.name;
        button.dataset.aaProductValue = state.productSizeValue || '';
        button.disabled = state.name !== 'available';
        button.setAttribute('aria-disabled', String(state.name !== 'available'));
        button.setAttribute('aria-pressed', String(Boolean(isSelected)));
        button.classList.toggle('is-selected', Boolean(isSelected));

        const status = button.querySelector('[data-aa-size-status]');
        if (status) status.textContent = state.label;
      });
    }

    handleDocumentClick(event) {
      const trigger = event.target.closest('[data-aa-action="size-guide-open"]');
      if (!trigger || trigger.dataset.aaSectionId !== this.sectionId) return;

      const wasOpen = this.modal.hasAttribute('open');
      this.refresh();

      window.requestAnimationFrame(() => {
        if (wasOpen || !this.isConnected || !this.modal?.hasAttribute('open')) return;

        this.refresh();
        const availableSizeCount = this.modal.querySelectorAll(
          '[data-aa-action="size-select"][data-aa-state="available"]:not(:disabled)'
        ).length;

        this.pushAnalytics('aa_size_guide_open', {
          size_system: this.sizeSystem,
          available_size_count: availableSizeCount,
        });
      });
    }

    handleDocumentChange(event) {
      const picker = event.target.closest('variant-selects');
      if (!picker || picker.dataset.section !== this.sectionId) return;
      this.refresh();
    }

    handleModalClick(event) {
      const button = event.target.closest('[data-aa-action="size-select"]');
      if (!button || button.disabled) return;

      this.refresh();
      if (button.dataset.aaState !== 'available') return;

      const picker = this.picker;
      const productSizeValue = button.dataset.aaProductValue;
      const selections = this.getCurrentSelections();
      selections[this.sizeIndex] = productSizeValue;

      const validVariant = this.config.variants.find(
        (variant) =>
          variant.available &&
          variant.options.every((value, index) => normalizeValue(value) === normalizeValue(selections[index]))
      );

      if (!picker || !validVariant) {
        this.announce('Esta talla no está disponible con las opciones seleccionadas.');
        this.refresh();
        return;
      }

      const radioInput = Array.from(picker.querySelectorAll('input[type="radio"][data-option-name]')).find(
        (input) =>
          input.dataset.optionName === this.config.optionName &&
          normalizeValue(input.value) === normalizeValue(productSizeValue)
      );

      if (radioInput) {
        radioInput.checked = true;
        radioInput.dispatchEvent(new Event('change', { bubbles: true }));
        this.trackSizeSelection(button, productSizeValue);
        this.completeSelection(button.dataset.aaLetter);
        return;
      }

      const selectOption = Array.from(picker.querySelectorAll('option[data-option-name]')).find(
        (option) =>
          option.dataset.optionName === this.config.optionName &&
          normalizeValue(option.value) === normalizeValue(productSizeValue)
      );

      if (selectOption) {
        const select = selectOption.closest('select');
        select.value = selectOption.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        this.trackSizeSelection(button, productSizeValue);
        this.completeSelection(button.dataset.aaLetter);
        return;
      }

      this.announce('No pudimos seleccionar esta talla. Inténtalo de nuevo.');
    }

    trackSizeSelection(button, productSizeValue) {
      const selectedValue = String(productSizeValue).trim();
      const isNumericSelection = normalizeValue(selectedValue) === normalizeValue(button.dataset.aaNumber);

      this.pushAnalytics('aa_size_selected', {
        size_selected: selectedValue,
        size_equivalent: isNumericSelection ? button.dataset.aaLetter : button.dataset.aaNumber,
        size_system: this.sizeSystem,
        availability_state: 'available',
      });
    }

    pushAnalytics(eventName, parameters) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: eventName,
        event_schema_version: 1,
        page_context: 'product',
        ...parameters,
      });
    }

    completeSelection(size) {
      this.announce(`Talla ${size} seleccionada.`);
      this.modal.hide();
    }

    announce(message) {
      if (!this.liveRegion) return;
      this.liveRegion.textContent = '';
      window.requestAnimationFrame(() => {
        this.liveRegion.textContent = message;
      });
    }
  }

  customElements.define('aa-size-finder', AASizeFinder);
})();
